import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, developerApi, formatIst, inboxApi } from '@wa/shared';
import type { ApiKeySummary, WebhookDelivery, WebhookSubscription } from '@wa/shared';

/**
 * Keys and webhooks — the tenant's half of the public API.
 *
 * Two things on this screen are shown exactly once and then never again: an API key
 * and a webhook signing secret. Both are stored as something you cannot read back — a
 * hash and a ciphertext — so the page says so before the value disappears rather than
 * after somebody has closed the dialog.
 */
export function DeveloperPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ label: string; value: string } | null>(null);

  const keys = useQuery({ queryKey: ['api-keys'], queryFn: developerApi.apiKeys });
  const hooks = useQuery({ queryKey: ['webhooks'], queryFn: developerApi.webhooks });
  const events = useQuery({ queryKey: ['webhook-events'], queryFn: developerApi.webhookEvents });

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);
  const refresh = (key: string) => void queryClient.invalidateQueries({ queryKey: [key] });

  const createKey = useMutation({
    mutationFn: developerApi.createApiKey,
    onSuccess: (made) => {
      setError(null);
      refresh('api-keys');
      setRevealed({ label: 'Your new API key', value: made.key });
    },
    onError: (e) => fail(e, 'Could not create that key.'),
  });

  const revokeKey = useMutation({
    mutationFn: developerApi.revokeApiKey,
    onSuccess: () => { setError(null); refresh('api-keys'); },
    onError: (e) => fail(e, 'Could not revoke that key.'),
  });

  const createHook = useMutation({
    mutationFn: developerApi.createWebhook,
    onSuccess: (made) => {
      setError(null);
      refresh('webhooks');
      setRevealed({ label: 'Your webhook signing secret', value: made.secret });
    },
    onError: (e) => fail(e, 'Could not create that webhook.'),
  });

  const patchHook = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: { isActive?: boolean } }) => developerApi.updateWebhook(id, patch),
    onSuccess: () => { setError(null); refresh('webhooks'); },
    onError: (e) => fail(e, 'Could not change that webhook.'),
  });

  const testHook = useMutation({
    mutationFn: developerApi.testWebhook,
    onSuccess: () => { setError(null); refresh('webhooks'); },
    onError: (e) => fail(e, 'Could not queue a test delivery.'),
  });

  return (
    <div className="flex flex-col gap-6">
      {error && (
        <p role="alert" className="rounded-(--radius-card) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {revealed && (
        <div className="rounded-(--radius-card) border border-(--color-brand) bg-(--color-sunken) p-4">
          <p className="text-small font-medium">{revealed.label}</p>
          <p className="mt-1 text-small text-(--color-muted)">
            Copy it now. It is stored in a form we cannot read back, so this is the only time it
            is ever shown.
          </p>
          <code className="mt-2 block overflow-x-auto rounded bg-(--color-paper) px-3 py-2 font-mono text-small break-all">
            {revealed.value}
          </code>
          <button
            type="button"
            onClick={() => setRevealed(null)}
            className="tap mt-3 rounded-(--radius-chip) border border-(--color-line) px-3 py-1.5 text-small"
          >
            I have copied it
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------ keys -- */}
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-body font-semibold">API keys</h2>
          <p className="mt-1 max-w-prose text-small text-(--color-muted)">
            A key acts as this account, limited to the things you tick. Give an integration the
            narrowest set that does its job &mdash; a key that can only read contacts cannot launch a
            broadcast if it leaks.
          </p>
        </div>

        <KeyForm
          busy={createKey.isPending}
          onCreate={(name, scopes) => createKey.mutate({ name, scopes })}
        />

        <div className="overflow-x-auto rounded-(--radius-card) border border-(--color-line)">
          <table className="w-full border-collapse text-small">
            <thead>
              <tr className="text-micro tracking-wide text-(--color-muted) uppercase">
                <Th>Name</Th><Th>Key</Th><Th>Can do</Th>
                <Th className="hidden @min-[34rem]:table-cell">Last used</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(keys.data ?? []).map((key: ApiKeySummary) => (
                <tr key={key.id} className="border-t border-(--color-line)">
                  <Td>{key.name}</Td>
                  <Td><code className="font-mono text-micro">{key.keyPrefix}…</code></Td>
                  <Td>
                    {key.scopes === null
                      ? <span className="text-(--color-warn)">everything</span>
                      : <span className="break-words">{key.scopes.split(' ').join(', ')}</span>}
                  </Td>
                  <Td className="hidden @min-[34rem]:table-cell">{formatIst(key.lastUsedAt, 'never')}</Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => revokeKey.mutate(key.id)}
                      className="tap rounded-(--radius-chip) border border-(--color-line) px-2 py-1 text-micro whitespace-nowrap hover:text-(--color-bad)"
                    >
                      Revoke
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          {(keys.data?.length ?? 0) === 0 && (
            <p className="px-3 py-6 text-center text-small text-(--color-muted)">No keys yet.</p>
          )}
        </div>
      </section>

      {/* -------------------------------------------------------- webhooks -- */}
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-body font-semibold">Webhooks</h2>
          <p className="mt-1 max-w-prose text-small text-(--color-muted)">
            We POST to your address when something happens, signed with the secret shown once at
            creation. Deliveries are retried with backoff; an endpoint that fails twenty times
            running is paused and told to you here rather than retried for ever.
          </p>
        </div>

        <HookForm
          events={events.data ?? []}
          busy={createHook.isPending}
          onCreate={(url, chosen) => createHook.mutate({ url, events: chosen })}
        />

        <div className="flex flex-col gap-3">
          {(hooks.data ?? []).map((hook: WebhookSubscription) => (
            <Hook
              key={hook.id}
              hook={hook}
              onTest={() => testHook.mutate(hook.id)}
              onToggle={() => patchHook.mutate({ id: hook.id, patch: { isActive: !hook.isActive } })}
            />
          ))}
          {(hooks.data?.length ?? 0) === 0 && (
            <p className="rounded-(--radius-card) border border-(--color-line) px-3 py-6 text-center text-small text-(--color-muted)">
              No webhooks yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium ${className}`}>{children}</th>;
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

/** The permissions a key may be given. Read from the same catalogue the roles screen uses. */
function KeyForm({ busy, onCreate }: {
  busy: boolean; onCreate: (name: string, scopes: string[]) => void;
}) {
  const catalogue = useQuery({ queryKey: ['permissions'], queryFn: inboxApi.permissionCatalogue });
  const [open, setOpen] = useState(false);
  // Nothing pre-ticked: a key's scopes are a decision, and a default that happens
  // to be right for one integration is a default that is silently wrong for the next.
  const [chosen, setChosen] = useState<string[]>([]);

  if (!open) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="tap rounded-(--radius-chip) bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink)"
        >
          New key
        </button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-(--radius-card) border border-(--color-line) p-3"
      onSubmit={(e) => {
        e.preventDefault();
        const name = String(new FormData(e.currentTarget).get('name') ?? '').trim();
        if (name && chosen.length > 0) onCreate(name, chosen);
        setOpen(false);
      }}
    >
      <label className="text-small">
        <span className="mb-1 block text-(--color-muted)">
          What is it for? You will want to recognise this in six months.
        </span>
        <input
          name="name" required autoFocus placeholder="Order status sync"
          className="w-full rounded-(--radius-chip) border border-(--color-line) bg-(--color-paper) px-2 py-1.5 text-body"
        />
      </label>

      <fieldset className="text-small">
        <legend className="mb-1 text-(--color-muted)">This key may</legend>
        <div className="grid gap-1 @min-[30rem]:grid-cols-2">
          {(catalogue.data ?? []).map((permission) => (
            <label key={permission.key} className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={chosen.includes(permission.key)}
                onChange={(e) => setChosen(e.target.checked
                  ? [...chosen, permission.key]
                  : chosen.filter((k) => k !== permission.key))}
              />
              <span>
                {permission.label}
                <span className="block font-mono text-micro text-(--color-muted)">{permission.key}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex gap-2">
        <button
          type="submit" disabled={busy || chosen.length === 0}
          className="tap rounded-(--radius-chip) bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-50"
        >
          Create
        </button>
        <button
          type="button" onClick={() => setOpen(false)}
          className="tap rounded-(--radius-chip) border border-(--color-line) px-3 py-1.5 text-small"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function HookForm({ events, busy, onCreate }: {
  events: string[]; busy: boolean; onCreate: (url: string, events: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);

  if (!open) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="tap rounded-(--radius-chip) bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink)"
        >
          New webhook
        </button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-(--radius-card) border border-(--color-line) p-3"
      onSubmit={(e) => {
        e.preventDefault();
        const url = String(new FormData(e.currentTarget).get('url') ?? '').trim();
        if (url) onCreate(url, chosen);
        setOpen(false);
      }}
    >
      <label className="text-small">
        <span className="mb-1 block text-(--color-muted)">
          Where should we POST? It has to be https, and it cannot be an address on a private network.
        </span>
        <input
          name="url" type="url" required autoFocus placeholder="https://example.com/hooks/whatsapp"
          className="w-full rounded-(--radius-chip) border border-(--color-line) bg-(--color-paper) px-2 py-1.5 text-body"
        />
      </label>

      <fieldset className="text-small">
        <legend className="mb-1 text-(--color-muted)">Tell me about — leave all unticked for everything</legend>
        <div className="grid gap-1 @min-[30rem]:grid-cols-2">
          {events.map((name) => (
            <label key={name} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={chosen.includes(name)}
                onChange={(e) => setChosen(e.target.checked
                  ? [...chosen, name]
                  : chosen.filter((k) => k !== name))}
              />
              <span className="font-mono text-micro">{name}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex gap-2">
        <button
          type="submit" disabled={busy}
          className="tap rounded-(--radius-chip) bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-50"
        >
          Create
        </button>
        <button
          type="button" onClick={() => setOpen(false)}
          className="tap rounded-(--radius-chip) border border-(--color-line) px-3 py-1.5 text-small"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** One subscription, with its recent deliveries on demand. */
function Hook({ hook, onTest, onToggle }: {
  hook: WebhookSubscription; onTest: () => void; onToggle: () => void;
}) {
  const [showLog, setShowLog] = useState(false);

  const log = useQuery({
    queryKey: ['webhook-deliveries', hook.id],
    queryFn: () => developerApi.webhookDeliveries(hook.id),
    enabled: showLog,
  });

  return (
    <div className="rounded-(--radius-card) border border-(--color-line) p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-small break-all">{hook.url}</p>
          <p className="mt-0.5 text-micro text-(--color-muted)">
            {hook.events ? hook.events.split(' ').join(', ') : 'every event'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button type="button" onClick={onTest}
                  className="tap rounded-(--radius-chip) border border-(--color-line) px-2 py-1 text-micro">
            Send a test
          </button>
          <button type="button" onClick={() => setShowLog(!showLog)}
                  className="tap rounded-(--radius-chip) border border-(--color-line) px-2 py-1 text-micro">
            {showLog ? 'Hide log' : 'Deliveries'}
          </button>
          <button type="button" onClick={onToggle}
                  className="tap rounded-(--radius-chip) border border-(--color-line) px-2 py-1 text-micro">
            {hook.isActive ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      </div>

      {hook.pausedAt && (
        <p className="mt-2 rounded-(--radius-chip) border border-(--color-warn) px-2 py-1.5 text-micro text-(--color-warn)">
          {hook.pauseReason} Fix your endpoint and turn it back on.
        </p>
      )}

      {showLog && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-micro">
            <thead>
              <tr className="text-(--color-muted)">
                <Th>When</Th><Th>Event</Th><Th>Result</Th><Th>Tries</Th><Th>Why not</Th>
              </tr>
            </thead>
            <tbody>
              {(log.data ?? []).map((delivery: WebhookDelivery) => (
                <tr key={delivery.id} className="border-t border-(--color-line)">
                  <Td>{formatIst(delivery.createdAt)}</Td>
                  <Td><code className="font-mono">{delivery.event}</code></Td>
                  <Td>
                    <span className={delivery.status === 1 ? 'text-(--color-good)' : delivery.status === 2 ? 'text-(--color-bad)' : ''}>
                      {delivery.status === 1 ? 'delivered' : delivery.status === 2 ? 'gave up' : 'waiting'}
                      {delivery.lastStatusCode ? ` (${delivery.lastStatusCode})` : ''}
                    </span>
                  </Td>
                  <Td>{delivery.attempts}</Td>
                  <Td className="break-words">{delivery.lastError ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          {(log.data?.length ?? 0) === 0 && !log.isLoading && (
            <p className="px-3 py-4 text-center text-(--color-muted)">Nothing delivered yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
